// redux/actions/userProjectsAction.ts
import { createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../axios';
import { Project, Pagination } from '../types/projectTypes';
import { UserProjectsResponse, UserDashboardResponse } from '../types/userProjectTypes';

interface FetchUserProjectsParams {
  userId: string;
  page?: number;
  limit?: number;
  search?: string;
}

interface FetchUserProjectsByStatusParams {
  userId: string;
  status: string;
  page?: number;
  limit?: number;
  search?: string;
}

// Helper function to get auth token from cookies
const getAuthToken = (): string | null => {
  // Check for admin token first
  let token = document.cookie
    .split('; ')
    .find(row => row.startsWith('pms_admin_='))
    ?.split('=')[1];

  // If no admin token found, check for employee token
  if (!token) {
    token = document.cookie
      .split('; ')
      .find(row => row.startsWith('pms_employee_='))
      ?.split('=')[1];
  }

  // Add debug logging for token discovery
  console.log(
    '[getAuthToken] Found token type:',
    token ? (document.cookie.includes('pms_admin_=') ? 'admin' : 'employee') : 'none'
  );

  return token ? `Bearer ${token}` : null;
};

// Fetch projects assigned to the current user with proper pagination
export const fetchUserProjects = createAsyncThunk<
  UserProjectsResponse,
  FetchUserProjectsParams,
  { rejectValue: string }
>(
  'userProjects/fetchUserProjects',
  async ({ userId, page = 1, limit = 10, search }, { rejectWithValue }) => {
    try {
      console.log(
        `[Action] fetchUserProjects - Fetching user projects for user ${userId}, page ${page}, limit ${limit}${search ? `, search: ${search}` : ''}`
      );

      // Build API URL with search parameter if provided
      const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
      const apiUrl = `/v1/admin/project/user/${userId}?page=${page}&limit=${limit}${searchParam}`;
      console.log(`[Action] fetchUserProjects - Making API call to: ${apiUrl}`);

      // The axios instance already handles authorization headers in its interceptors
      const response = await axiosInstance.get<{
        success: boolean;
        data: Project[]; // Array of projects
        pagination?: {
          total: number;
          page: number;
          limit: number;
          totalPages: number;
          pages: number;
          hasNextPage: boolean;
          hasPrevPage: boolean;
          nextCursor?: string;
          totalItems?: number; // For compatibility
          itemsPerPage?: number; // For compatibility
        };
        dashboardData?: {
          userProjectCount: number;
          userPaymentReceived: number;
          userBilledAmount: number;
          userPipelineAmount: number;
          userStatusCounts: Record<string, number>;
        };
        message?: string;
      }>(apiUrl);

      console.log('[Action] fetchUserProjects response status:', response.status);
      console.log('[Action] fetchUserProjects response data:', response.data);

      if (!response.data.success) {
        console.error('[Action] fetchUserProjects - API returned success: false');
        console.error('[Action] fetchUserProjects - Error message:', response.data.message);
        return rejectWithValue(response.data.message || 'API returned unsuccessful response');
      }

      // Check if data is an array
      if (!Array.isArray(response.data.data)) {
        console.error(
          '[Action] fetchUserProjects - API returned invalid data format:',
          response.data
        );
        return rejectWithValue('Invalid data format received from API');
      }

      console.log('[Action] fetchUserProjects data count:', response.data.data.length);

      return {
        ...response.data,
        lastLoadTime: Date.now(),
      };
    } catch (error: any) {
      console.error('[Action] fetchUserProjects error:', error);
      console.error('[Action] fetchUserProjects error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });

      // Check if it's an authentication error
      if (error.response?.status === 401) {
        console.error('[Action] fetchUserProjects - Authentication error (401)');
        return rejectWithValue('Authentication failed. Please log in again.');
      }

      return rejectWithValue(
        error.response?.data?.message ||
          error.response?.data?.error?.message ||
          'Failed to fetch user projects'
      );
    }
  }
);

// Refresh user projects (force reload)
export const refreshUserProjects = createAsyncThunk<
  UserProjectsResponse,
  FetchUserProjectsParams,
  { rejectValue: string }
>(
  'userProjects/refreshUserProjects',
  async ({ userId, page = 1, limit = 10 }, { rejectWithValue }) => {
    try {
      console.log(`[Action] refreshUserProjects - Refreshing user projects for user ${userId}`);

      // Fix the API URL - remove the duplicate /api/ prefix and remove timestamp parameter
      const apiUrl = `/v1/admin/project/user/${userId}?page=${page}&limit=${limit}`;
      console.log(`[Action] refreshUserProjects - Making API call to: ${apiUrl}`);

      // The axios instance already handles authorization headers in its interceptors
      const response = await axiosInstance.get<{
        success: boolean;
        data: Project[];
        pagination?: {
          total: number;
          page: number;
          limit: number;
          totalPages: number;
          pages: number;
          hasNextPage: boolean;
          hasPrevPage: boolean;
          nextCursor?: string;
          totalItems?: number; // For compatibility
          itemsPerPage?: number; // For compatibility
        };
        dashboardData?: {
          userProjectCount: number;
          userPaymentReceived: number;
          userBilledAmount: number;
          userPipelineAmount: number;
          userStatusCounts: Record<string, number>;
        };
        message?: string;
      }>(apiUrl);

      console.log('[Action] refreshUserProjects response status:', response.status);
      console.log('[Action] refreshUserProjects response data:', response.data);

      if (!response.data.success) {
        return rejectWithValue(response.data.message || 'API returned unsuccessful response');
      }

      return {
        ...response.data,
        lastLoadTime: Date.now(),
      };
    } catch (error: any) {
      console.error('[Action] refreshUserProjects error:', error);
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to refresh user projects'
      );
    }
  }
);

// Fetch dashboard data for user projects
export const fetchUserProjectsDashboardData = createAsyncThunk<
  UserDashboardResponse,
  { startDate: string; endDate: string; forceRefresh?: boolean },
  { rejectValue: string }
>(
  'userProjects/fetchUserProjectsDashboardData',
  async (dateRange, { rejectWithValue, getState }) => {
    try {
      console.log(
        `[Action] fetchUserProjectsDashboardData - Fetching user dashboard data for date range: ${dateRange.startDate} to ${dateRange.endDate}, forceRefresh: ${dateRange.forceRefresh || false}`
      );

      // Get the auth state to verify we have a user
      const state = getState() as any;
      const user = state.auth?.user;

      if (!user || !user.id) {
        console.error('[Action] fetchUserProjectsDashboardData - No authenticated user found');
        return rejectWithValue('User not authenticated. Cannot fetch dashboard data.');
      }

      console.log(
        `[Action] fetchUserProjectsDashboardData - Fetching for user ID: ${user.id}, role: ${user.role || 'unknown'}`
      );

      // Check cookies to verify auth token
      const authToken = getAuthToken();
      console.log(
        '[Action] fetchUserProjectsDashboardData - Auth token found:',
        Boolean(authToken)
      );

      // Confirm the final URL to check if it's correct
      const apiUrl = `/v1/admin/project/dashboard/user?startDate=${dateRange.startDate}&endDate=${dateRange.endDate}`;
      console.log(
        `[Action] fetchUserProjectsDashboardData - NETWORK REQUEST - API call to: ${apiUrl}`
      );

      // Add explicit headers to ensure authentication is sent
      const headers = {
        'Content-Type': 'application/json',
      };

      const response = await axiosInstance.get<{
        success: boolean;
        data: {
          totalUserProjectsCount: number;
          projectsInRangeCount: number;
          financialMetrics: {
            paymentReceivedSum: number;
            billedSum: number;
            dueSum: number;
            pipelineSum: number;
          };
          dailyPayments: Array<{ date: string; amount: number }>;
          statusCounts: Record<string, number>; // Added statusCounts property
        };
      }>(apiUrl);

      console.log('[Action] fetchUserProjectsDashboardData response status:', response.status);
      console.log('[Action] fetchUserProjectsDashboardData response data:', response.data);

      if (!response.data.success) {
        console.error(
          '[Action] fetchUserProjectsDashboardData - API returned error:',
          response.data
        );
        return rejectWithValue('Failed to fetch user dashboard data');
      }

      // Initialize statusCounts if missing
      if (!response.data.data.statusCounts) {
        console.warn(
          '[Action] fetchUserProjectsDashboardData - statusCounts missing in API response, using empty object'
        );
        response.data.data.statusCounts = {};
      }

      return {
        success: true,
        data: response.data.data,
        timestamp: Date.now(),
      };
    } catch (error: any) {
      console.error('[Action] fetchUserProjectsDashboardData error:', error);
      console.error('[Action] fetchUserProjectsDashboardData error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        stack: error.stack,
      });

      // Check if it's an authentication error
      if (error.response?.status === 401) {
        console.error('[Action] fetchUserProjectsDashboardData - Authentication error (401)');
        return rejectWithValue('Authentication failed. Please log in again.');
      }

      return rejectWithValue(
        error.response?.data?.error?.message ||
          error.message ||
          'Failed to fetch user dashboard data'
      );
    }
  }
);

// New action to fetch user projects by status with proper pagination
export const fetchUserProjectsByStatus = createAsyncThunk<
  { data: Project[]; status: string; pagination?: any },
  FetchUserProjectsByStatusParams,
  { rejectValue: string }
>(
  'userProjects/fetchUserProjectsByStatus',
  async ({ userId, status, page = 1, limit = 10, search }, { rejectWithValue }) => {
    try {
      console.log(
        `[Action] fetchUserProjectsByStatus - Fetching user projects with status: ${status} for user ${userId}, page ${page}, limit ${limit}${search ? `, search: ${search}` : ''}`
      );

      // Normalize status for API query
      const apiStatus = status.replace(' ', '_').toUpperCase();
      const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
      const apiUrl = `/v1/admin/project/user/${userId}?status=${apiStatus}&page=${page}&limit=${limit}${searchParam}`;
      console.log(`[Action] fetchUserProjectsByStatus - Making API call to: ${apiUrl}`);

      const response = await axiosInstance.get<{
        success: boolean;
        data: Project[];
        pagination?: {
          total: number;
          page: number;
          limit: number;
          totalPages: number;
          pages: number;
          hasNextPage: boolean;
          hasPrevPage: boolean;
          nextCursor?: string;
          totalItems?: number; // For compatibility
          itemsPerPage?: number; // For compatibility
        };
        dashboardData?: {
          userProjectCount: number;
          userPaymentReceived: number;
          userBilledAmount: number;
          userPipelineAmount: number;
          userStatusCounts: Record<string, number>;
        };
        message?: string;
      }>(apiUrl);

      console.log('[Action] fetchUserProjectsByStatus response status:', response.status);
      console.log('[Action] fetchUserProjectsByStatus response data:', response.data);

      if (!response.data.success) {
        console.error('[Action] fetchUserProjectsByStatus - API returned success: false');
        return rejectWithValue(response.data.message || 'API returned unsuccessful response');
      }

      // Check if data is an array
      if (!Array.isArray(response.data.data)) {
        console.error(
          '[Action] fetchUserProjectsByStatus - API returned invalid data format:',
          response.data
        );
        return rejectWithValue('Invalid data format received from API');
      }

      console.log('[Action] fetchUserProjectsByStatus data count:', response.data.data.length);

      return {
        data: response.data.data,
        status, // Return the original status for caching
        pagination: response.data.pagination, // Include pagination data
      };
    } catch (error: any) {
      console.error('[Action] fetchUserProjectsByStatus error:', error);
      return rejectWithValue(
        error.response?.data?.message ||
          error.response?.data?.error?.message ||
          'Failed to fetch user projects by status'
      );
    }
  }
);
